import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const STORE_ID = '52f68b0c-bb0b-41ee-9529-f6dda58e6d04';
const API_V4_PREFIX = '/v4/99food';

const SyncButton = ({ label, color, onClick, busy, testId }) => (
  <Button
    type="button"
    onClick={onClick}
    disabled={busy}
    className={`${color} text-white flex items-center gap-2 disabled:opacity-60`}
    data-testid={testId}
  >
    {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
    {busy ? 'Enviando...' : label}
  </Button>
);

const formatIfoodResult = (data) => {
  // Mensagem clara para o user-final
  if (data?.message) return data.message;
  const t = data?.totals || {};
  const c = t.created ?? 0, u = t.updated ?? 0, s = t.skipped ?? 0, f = t.failed ?? 0;
  const remap = data?.remap || {};
  const remapped = remap.remapped || 0;
  const fixed = remap.fixed_phantom || 0;
  if (c === 0 && u === 0 && f === 0 && remapped === 0) {
    return `Cardápio já está em dia com o iFood — nenhuma alteração para enviar (${s} produtos verificados).`;
  }
  const parts = [];
  if (c > 0) parts.push(`${c} criado(s)`);
  if (u > 0) parts.push(`${u} atualizado(s)`);
  if (remapped > 0) parts.push(`${remapped} re-mapeado(s)${fixed ? ` (${fixed} fantasmas corrigidos)` : ''}`);
  if (f > 0) parts.push(`${f} falha(s)`);
  if (s > 0) parts.push(`${s} já em dia`);
  return `iFood: ${parts.join(', ')}`;
};

const formatGenericResult = (data) => {
  if (data?.message) return data.message;
  const cats = data?.categories ?? data?.summary?.categories ?? null;
  const prods = data?.products ?? data?.summary?.products ?? null;
  if (cats != null || prods != null) {
    return `Categorias: ${cats ?? '-'} | Produtos: ${prods ?? '-'}`;
  }
  return JSON.stringify(data).slice(0, 120);
};

export const CardapioSyncButtons = () => {
  const [busy, setBusy] = useState({ ifood: false, food99: false, keeta: false, import99: false });
  const [conn99, setConn99] = useState(null);

  // Carregar connection_id do 99Food
  useEffect(() => {
    api.get(`${API_V4_PREFIX}/admin/status`)
      .then((r) => {
        if (r?.data?.configured && r?.data?.connection?.id) setConn99(r.data.connection.id);
      })
      .catch(() => { /* silencioso */ });
  }, []);

  const syncIfood = async () => {
    setBusy(b => ({ ...b, ifood: true }));
    const tid = toast.loading('Sincronizando cardápio iFood...');
    try {
      const res = await api.post(`/ifood/v2/${STORE_ID}/full-sync`, {}, { timeout: 600000 });
      const ok = res?.data?.success !== false;
      const msg = formatIfoodResult(res?.data);
      ok ? toast.success(msg, { id: tid }) : toast.error(`iFood: ${res?.data?.message || msg}`, { id: tid });
    } catch (err) {
      // Detectar tipo de erro para mensagem clara
      let msg;
      if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        msg = 'Timeout: a sincronização demorou mais que o esperado. Os produtos podem ter sido sincronizados parcialmente — clique de novo para retomar.';
      } else if (err?.response?.status === 504 || err?.response?.status === 502) {
        msg = `Servidor indisponível (HTTP ${err.response.status}). Tente novamente em alguns segundos.`;
      } else if (err?.response?.data?.detail) {
        msg = `Erro: ${err.response.data.detail}`;
      } else if (err?.response?.data?.message) {
        msg = `Erro: ${err.response.data.message}`;
      } else if (err?.response?.status) {
        msg = `Erro HTTP ${err.response.status}: ${err.message}`;
      } else {
        msg = `Falha: ${err?.message || 'erro desconhecido'}`;
      }
      toast.error(msg, { id: tid, duration: 8000 });
      // Tambem loga detalhado no console
      console.error('[Sync iFood] erro completo:', err?.response || err);
    } finally {
      setBusy(b => ({ ...b, ifood: false }));
    }
  };

  const sync99Food = async () => {
    if (!conn99) {
      toast.error('99Food não configurado. Acesse a aba 99Food v4 para configurar.');
      return;
    }
    setBusy(b => ({ ...b, food99: true }));
    const tid = toast.loading('Sincronizando cardápio 99Food...');
    try {
      const res = await api.post(`${API_V4_PREFIX}/connections/${conn99}/menu/sync`, {}, { timeout: 240000 });
      toast.success('99Food: ' + formatGenericResult(res?.data), { id: tid });
    } catch (err) {
      toast.error('Falha 99Food: ' + (err?.response?.data?.detail || err?.response?.data?.message || err.message), { id: tid });
    } finally {
      setBusy(b => ({ ...b, food99: false }));
    }
  };

  const syncKeeta = async () => {
    setBusy(b => ({ ...b, keeta: true }));
    const tid = toast.loading('Sincronizando cardápio Keeta...');
    try {
      const res = await api.post('/keeta/menu/sync', { sync_type: 'FULL' }, { timeout: 240000 });
      toast.success('Keeta: ' + formatGenericResult(res?.data), { id: tid });
    } catch (err) {
      toast.error('Falha Keeta: ' + (err?.response?.data?.detail || err?.response?.data?.message || err.message), { id: tid });
    } finally {
      setBusy(b => ({ ...b, keeta: false }));
    }
  };

  const importPrices99Food = async () => {
    setBusy(b => ({ ...b, import99: true }));
    const tid = toast.loading('Importando preços do 99Food...');
    try {
      const res = await api.post('/products/import-prices-from-99food');
      const d = res?.data || {};
      toast.success('Importacao: ' + (d.updated ?? 0) + ' produto(s) atualizado(s)', { id: tid });
    } catch (err) {
      toast.error('Falha ao importar: ' + (err?.response?.data?.detail || err?.message), { id: tid });
    } finally {
      setBusy(b => ({ ...b, import99: false }));
    }
  };

  return (

    <div className="flex flex-wrap items-center gap-2" data-testid="cardapio-sync-buttons">
      <SyncButton label="Cardápio iFood" color="bg-orange-500 hover:bg-orange-600"
        onClick={syncIfood} busy={busy.ifood} testId="btn-sync-cardapio-ifood" />
      <SyncButton label="Cardápio 99Food" color="bg-green-600 hover:bg-green-700"
        onClick={sync99Food} busy={busy.food99} testId="btn-sync-cardapio-99food" />
      <SyncButton label="Cardápio Keeta" color="bg-red-500 hover:bg-red-600"
        onClick={syncKeeta} busy={busy.keeta} testId="btn-sync-cardapio-keeta" />
    </div>
  );
};

export default CardapioSyncButtons;
